import GeoJSON from "ol/format/GeoJSON";
import { AttributeValue } from "./AttributeValue";
import { ISearchDateAttributeConvert } from "./ISearchDateAttributeConvert";
import { FeatureType } from "@/geoMap/define/FeatureType";
import { getLayerKey } from "@/geoMap/LayerName";
import {
  TraVosLayerType,
  TraVosRoadType,
} from "@/geoMap/layerFactory/TraVosLayerName";
import { AttributeView } from "./AttributeView";
import { TraVosCodeUtil } from "./util/TraVosCodeUtil";
import { ExportValue } from "./ExportValue";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { parseTimeCode } from "./util/TimeCodeUtil";
import { Geometry } from "ol/geom";

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * 対象FeatureType
 */
const FEATURE_TYPE = FeatureType.TraVosLinkSearchDateAttributeConvert;

const PROPERTY_MAP = [
  { key: "交通調査基本区間番号", isSimple: true, isCommon: true },
  { key: "算定年月日", isSimple: true, isCommon: false },
  { key: "指定時間帯", isSimple: true, isCommon: false },
  { key: "上り・小型交通量", isSimple: true, isCommon: false },
  { key: "上り・大型交通量", isSimple: true, isCommon: false },
  { key: "下り・小型交通量", isSimple: true, isCommon: false },
  { key: "下り・大型交通量", isSimple: true, isCommon: false },
  { key: "地方整備局等番号", isSimple: false, isCommon: false },
  { key: "開発建設部／都道府県コード", isSimple: false, isCommon: false },
  { key: "世代管理番号（十の位）", isSimple: false, isCommon: false },
  { key: "世代管理番号（一の位）", isSimple: false, isCommon: false },
  { key: "区間識別フラグ", isSimple: false, isCommon: false },
  { key: "交通量調査単位区間バージョン", isSimple: false, isCommon: false },
  {
    key: "交通量調査単位区間の都道府県指定市コード",
    isSimple: false,
    isCommon: false,
  },
  { key: "交通量調査単位区間番号", isSimple: false, isCommon: false },
  { key: "観測フラグ", isSimple: false, isCommon: false },
  {
    key: "基準年月（方向別車種別時間交通量）",
    isSimple: false,
    isCommon: false,
  },
  { key: "基準年月（断面昼夜率)", isSimple: false, isCommon: false },
  { key: "算定年月", isSimple: false, isCommon: false },
  { key: "曜日コード", isSimple: false, isCommon: false },
  { key: "平日・休日コード", isSimple: false, isCommon: false },
];
/**
 * 画面フォーマット用大型交通量判別定数
 */
const LARGE_CAR_TRAFFIC_VOLUME = "大型交通量";
/**
 * 画面フォーマット用小型交通量判別定数
 */
const SMALL_CAR_TRAFFIC_VOLUME = "小型交通量";

/**
 * 画面幅
 */
const VALUE_WIDTH = 506;

/**
 * 画面高さ
 */
const VALUE_HEIGTH = 300;

/**
 * 画面最大高さ
 */
const VALUE_MAX_HEIGHT = 400;

/**
 * 画面タイトル
 */
const VALUE_TITLE = "交通量データ（過去）属性表示画面(交通調査基本区間)";

/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME =
  "traf_q-11-2_yyyyMMdd_HHmmss.csv.geojson";

/**
 * 出力CSVファイル名
 */
const VALUE_CSV_FILE_NAME = "traf_q-11-2_yyyyMMdd_HHmmss.csv.csv";

/**
 * 過去データTraVos属性表示画面(交通調査基本区間)用過去属性取得クラス
 */
export class TraVosLinkSearchDateAttributeConvert
  implements ISearchDateAttributeConvert
{
  /**
   * GeoJSON
   */
  private _geoJSON: any;
  /**
   * TraVosレイヤ名型定義
   */
  private _layerType: TraVosLayerType;
  /**
   * TraVos道路種別コード
   */
  private _roadType: TraVosRoadType;

  /**
   * 交通調査基本区間番号
   */
  private _trafficSurveySectionNumber: string;

  /**
   * TraVos道路種別コード
   */
  private _timeCode: string;

  /**
   * コンストラクタ
   * @param geoJSON GeoJSON
   * @param layerType TraVosレイヤタイプ
   * @param roadType TraVos道路種別
   * @param trafficSurveySectionNumber 交通調査基本区間番号
   * @param timeCode 時間コード
   */
  constructor(
    geoJSON: any,
    layerType: TraVosLayerType,
    roadType: TraVosRoadType,
    trafficSurveySectionNumber: string,
    timeCode: string
  ) {
    this._geoJSON = geoJSON;
    this._layerType = layerType;
    this._roadType = roadType;
    this._trafficSurveySectionNumber = trafficSurveySectionNumber;
    this._timeCode = timeCode;
  }

  /**
   * 属性データ更新
   * @param timeCode 時間コード
   */
  // 地物情報をtimeCodeで取得なおして、結果やtimeCodeを保持しておく
  async load(timeCode: string): Promise<ISearchDateAttributeConvert> {
    // 1時間属性：分単位は切り捨て
    const formattedTimeCode =
      this._timeCode.slice(8) === "2400"
        ? timeCode.slice(0, 8) + "2400"
        : timeCode.slice(0, 10) + "00";
    const layerKey = getLayerKey(this._layerType).replace("v_", "t_");
    const cqlQuery = `"道路種別"='${this._roadType}' AND "時間コード" = ${formattedTimeCode} AND "交通調査基本区間番号" = '${this._trafficSurveySectionNumber}'`;
    const cqlParameter = new CqlParameter(layerKey, cqlQuery);
    const WFSGetter = new WFSGetFeatureManager();
    const loadedGeoJSON = await WFSGetter.getInfo(URL_API, [
      cqlParameter,
    ]).catch((error) => {
      throw error;
    });
    return new TraVosLinkSearchDateAttributeConvert(
      loadedGeoJSON,
      this._layerType,
      this._roadType,
      this._trafficSurveySectionNumber,
      formattedTimeCode
    );
  }

  /**
   * 時間コード取得
   */
  getTimeCode(): string {
    return this._timeCode;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FEATURE_TYPE;
  }

  /**
   * 画面用フォーマット取得
   */
  getViewFormatData(): AttributeView {
    const attributeValues: AttributeValue[] = [];
    const features = new GeoJSON().readFeatures(this._geoJSON);
    const { year, month, day, hour, minute } = parseTimeCode(this._timeCode);
    if (features.length) {
      const feature = features[0];
      // 画面項目に基づき値を取得
      PROPERTY_MAP.forEach((property) => {
        if (property.key === PROPERTY_MAP[0].key) {
          /**
           * 交通調査基本区間番号(クラス変数)
           */
          attributeValues.push({
            key: property.key,
            value: `${this._trafficSurveySectionNumber}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[1].key) {
          /*
           * 算定年月日
           */
          attributeValues.push({
            key: property.key,
            value: `${year}/${month}/${day}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[2].key) {
          /**
           * 指定時間帯
           */
          const timeValueLabel = hour === "24" ? "24時間" : `${hour}:${minute}`;
          attributeValues.push({
            key: property.key,
            value: timeValueLabel,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (
          property.key.indexOf(LARGE_CAR_TRAFFIC_VOLUME) !== -1 ||
          property.key.indexOf(SMALL_CAR_TRAFFIC_VOLUME) !== -1
        ) {
          /**
           * 交通量データ系のラベルに単位を追加する
           */
          attributeValues.push({
            key: property.key + "（台／時間）",
            value: feature.get(property.key) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[7].key) {
          /**
           * 地方整備局等番号を画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toRegionalBureauViewString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[8].key) {
          /**
           * 開発建設部／都道府県コ－ドを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toPrefectureCodeViewString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[11].key) {
          /**
           * 区間識別フラグを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toSectorldentification(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[15].key) {
          /**
           * 観測フラグを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toObservationFlagsString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[17].key) {
          /*
           * 基準年月（断面昼夜率)
           */
          attributeValues.push({
            key: "基準年月（断面昼夜率）",
            value: feature.get(property.key) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[18].key) {
          /*
           * 算定年月
           */
          attributeValues.push({
            key: property.key,
            value: `${year}/${month}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[19].key) {
          /**
           * 曜日コードを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value: TraVosCodeUtil.toDayOfWeek(feature.get(property.key)) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[20].key) {
          /**
           * 平日・休日コードを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toWeekdayAndHoliday(feature.get(property.key)) ??
              "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else {
          attributeValues.push({
            key: property.key,
            value: feature.get(property.key) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        }
      });
    } else {
      // 画面項目に基づき値を取得
      PROPERTY_MAP.forEach((property) => {
        if (property.key === PROPERTY_MAP[0].key) {
          /**
           * 交通調査基本区間番号(クラス変数)
           */
          attributeValues.push({
            key: property.key,
            value: `${this._trafficSurveySectionNumber}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[1].key) {
          /*
           * 算定年月日
           */
          attributeValues.push({
            key: property.key,
            value: `${year}/${month}/${day}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[2].key) {
          /**
           * 指定時間帯
           */
          const timeValueLabel = hour === "24" ? "24時間" : `${hour}:${minute}`;
          attributeValues.push({
            key: property.key,
            value: timeValueLabel,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (
          property.key.indexOf(LARGE_CAR_TRAFFIC_VOLUME) !== -1 ||
          property.key.indexOf(SMALL_CAR_TRAFFIC_VOLUME) !== -1
        ) {
          /**
           * 交通量データ系のラベルに単位を追加する
           */
          attributeValues.push({
            key: property.key + "（台／時間）",
            value: "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else {
          attributeValues.push({
            key: property.key,
            value: "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        }
      });
    }
    const attributeView: AttributeView = {
      width: VALUE_WIDTH,
      height: VALUE_HEIGTH,
      maxheight: VALUE_MAX_HEIGHT,
      title: VALUE_TITLE,
      attributeValues: attributeValues,
    };

    return attributeView;
  }

  /**
   * ファイル出力用項目取得
   */
  getExportValue(): ExportValue {
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");
    const attributeValues = this.getViewFormatData().attributeValues;
    return new ExportValue(
      feature,
      attributeValues,
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }

  /**
   * ジオメトリを取得
   */
  getGeometry(): Geometry {
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    return feature?.getGeometry().transform("EPSG:3857", "EPSG:4326");
  }
}
